/**
 * /me/attendance/period — Session 3d PR 20.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { AttendancePeriodRow } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyAttendancePeriodPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-attendance-period", employeeId],
    queryFn: () => session3dApi.listAttendancePeriod(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<AttendancePeriodRow>[] = [
    {
      headerKey: "me.attendance-period.col.date",
      label: "Date",
      render: (r) => <span dir="ltr" className="text-xs">{r.date}</span>,
    },
    {
      headerKey: "me.attendance-period.col.in",
      label: "Clock in",
      render: (r) => <span className="font-mono text-xs">{r.clock_in ?? "—"}</span>,
    },
    {
      headerKey: "me.attendance-period.col.out",
      label: "Clock out",
      render: (r) => <span className="font-mono text-xs">{r.clock_out ?? "—"}</span>,
    },
    {
      headerKey: "me.attendance-period.col.worked",
      label: "Worked (hrs)",
      render: (r) => <span className="font-mono">{r.worked_hours.toFixed(1)}</span>,
    },
    {
      headerKey: "me.attendance-period.col.late",
      label: "Late (min)",
      render: (r) => <span className={`font-mono text-xs ${r.late_minutes > 0 ? "text-amber-700" : ""}`}>{r.late_minutes}</span>,
    },
    {
      headerKey: "me.attendance-period.col.ot",
      label: "OT (hrs)",
      render: (r) => <span className="font-mono text-xs">{r.overtime_hours.toFixed(1)}</span>,
    },
  ];

  return (
    <MeListPage<AttendancePeriodRow>
      title={t("me.attendance-period.title", "This Period (Attendance)")}
      subtitle={t("me.attendance-period.subtitle", "Your current period attendance summary.")}
      domain="attendance-period"
      khidmatPrefill={t("me.attendance-period.ask_khidmat", "Show me my attendance for this period.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.date}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.attendance-period.title", "This Period (Attendance)"),
        window_label: t("me.attendance-period.window", "current period"),
      }}
      emptyHeadline={t("me.attendance-period.empty_headline", "No attendance records this period.")}
      emptyBody={t("me.attendance-period.empty_body", "Khidmat: records appear once you clock in.")}
    />
  );
}
